import { IamData } from 'features/iam/services/user-service';
import { ResponseTimeAnalytics } from '/models/websockets.types';

export interface Mention {
  userId: string;           // unique ID
  trigger: string;          // the actual text typed, e.g. "@john"
  start: number;            // where mention starts in content
  end: number;              // where mention ends in content
  user?: IamData;          // optional: prefetched user object for UI
}

export enum MessageRole {
  ADD_PARTICIPANT = 'add-participant',
  CONVERSATION_TITLE_UPDATED = 'conversation-title-updated',
  USER = 'user',
  ASSISTANT = 'assistant',
  USER_REMOVED = 'user-removed',
  USER_CHAT = 'user',
  USER_ASSISTANT_CHAT = 'user-assistant-chat',
  ASSISTANT_CHAT = 'assistant-chat',
  MODEL_THINKING = 'model-thinking',
  CONVERSATION_CREATED = 'conversation-created',
}

export enum FileGenerationStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

export interface Attachment {
  fileId: string;
  fileName: string;
  extension: string;
  sender: MessageRole;
  status?: FileGenerationStatus;
}

export interface FileMetadata {
  fileId: string;
  fileName: string;
  extension: string;
  createdAt: string;
  updatedAt: string;
  size: number;
  sender: MessageRole;
}

export enum Tools {
  SEARCH = 'search',
  CREATE_IMAGE = 'create_image',
  VOICE = 'voice',
}

export interface Source {
  fileId: string;
  fileName: string;
  chunks: any[];
}

export interface SearchSource {
  pageContent: string;
  metadata: {
    title: string;
    url: string;
  }
}

export interface RelativeSearchQuestion {
  title: string;
  imgSrc: string | null;
  url: string | null;
  iframeSrc: string | null;
}

export interface ResponseAnalytics {
  timeToFirstToken: number;
  totalResponseTime: number;
  timePerOutputToken: number;
}

export interface MessageGeneration {
  id: string;
  content: string;
  createdDate?: number;
  thinkContent?: string;
  feedback?: boolean;
  attachments?: Attachment[];
  generatedImages?: Attachment[];
  HasFileGeneratedResponse?: boolean;
  sources?: Source[];
  searchSources?: SearchSource[];
  isThinking?: boolean;
  relatedSearcheQuestionsList?: RelativeSearchQuestion[];
  relatedSearcheImageList?: RelativeSearchQuestion[];
  relatedSearcheVideoList?: RelativeSearchQuestion[];
  hasSearchQuery?: boolean;
  isSkeletonShowForImages?: boolean;
  isSkeletonShowForVideos?: boolean;
  isSkeletonShowForQuestions?: boolean;
  isSearchSourcesShow?: boolean;
  isAnalyzed?: boolean;
  isAnalyzing?: boolean;
  codeExecutionSteps?: any[];
  parentIds?: string[];
  parentUserGenerationId?: string;
  parentMessage?: {
    senderName: string;
    content: string;
    time: number;
  };
  mentions?: Mention[];
  responseAnalytics?: ResponseTimeAnalytics;
  usedModelId?: string;
  isThisGenerationOnGoing?: boolean;
}

export enum MessageDeliveryStatus {
  PENDING = 'pending',
  DELIVERED = 'delivered',
  FAILED = 'failed',
}

export interface Message {
  role: MessageRole;
  recordId: string;
  userRecordId?: string; // For assistant messages, keeps reference to the user's recordId
  idToReplaceWith?: string;
  userMessage?: {
    userName: string;
    message: string;
  };
  activeGenerationIndex?: number;
  toolsStatus?: Record<Tools, boolean>;
  senderId?: string;
  generations: MessageGeneration[];
  deliveryStatus?: MessageDeliveryStatus; // Only for user messages in group chats
}

export interface ReplyMessageData {
  generation: MessageGeneration;
  repliedUser?: {
    itemId: string;
    profileImageUrl: string | null;
    firstName: string;
    lastName: string | null;
    email: string;
    [key: string]: any;
  };
}

export interface VectorUploadItem {
  fileId: string;
}

export enum ButtonIds {
  SEARCH,
  CREATE_IMAGE,
  VOICE,
}

export const initialFeaturesState: Record<ButtonIds, boolean> = {
  [ButtonIds.SEARCH]: false,
  [ButtonIds.CREATE_IMAGE]: false,
  [ButtonIds.VOICE]: false,
};